import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for fetching governance statistics
 */
export function useStatistics() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 't24_get_statistics',
        nonce: window.t24Governance.nonce
      });

      const response = await fetch(
        `${window.t24Governance.ajaxUrl}?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const data = await response.json();

      if (data.success && data.data) {
        setStatistics(data.data.statistics);
      } else {
        throw new Error(data.data?.message || 'Failed to load statistics');
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.message);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Calculate completion rate
  const getCompletionRate = useCallback(() => {
    if (!statistics || !statistics.by_status) return 0;

    const total = Object.values(statistics.by_status).reduce((sum, count) => sum + count, 0);
    const completed = statistics.by_status.completed || 0;

    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [statistics]);

  // Get most active category
  const getMostActiveCategory = useCallback(() => {
    if (!statistics || !statistics.by_category) return null;

    const entries = Object.entries(statistics.by_category);
    if (entries.length === 0) return null;

    const [category, count] = entries.reduce((max, entry) =>
      entry[1] > max[1] ? entry : max
    );

    return { category, count };
  }, [statistics]);

  // Calculate participation rate
  const getParticipationRate = useCallback(() => {
    if (!statistics) return 0;

    const totalProposals = Object.values(statistics.by_status || {}).reduce((sum, count) => sum + count, 0);
    const totalVotes = statistics.total_votes || 0;

    return totalProposals > 0 ? Math.round((totalVotes / totalProposals) * 100) : 0;
  }, [statistics]);

  return {
    statistics,
    loading,
    error,
    refetch: fetchStatistics,
    getCompletionRate,
    getMostActiveCategory,
    getParticipationRate
  };
}
